import type { JobDataResultQuery } from "./__generated__/jobDataResult.gqlgenerated";
import type { ViewjobQuery } from "./__generated__/viewjob.gqlgenerated";
import type { GettextInterface, Locale } from "../../shared/I18n";
import type { TareData } from "../../shared/native-modules/tare/TareDataContext";
import type { FindResumeEngagementData } from "./components/core/JobComponent/EmployerOutreach/ResumeEngagementDataBuilder";
import type { ViewJobThirdPartyApply } from "./components/core/JobComponent/ViewJobButtonLink/ApplyButtonTypes";
import type {
  JobDetailsSectionModel,
  SalaryInfoModel,
} from "./components/core/ProfileInsights/components/PreferencesCollection/types";
import type { ApplicantInsightsModel } from "./components/mosaic/ApplicantInsights/types";

import { CookieKey } from "../../shared/native-modules/types/RNCoreNativeModule";
import { getCtkTimestamp } from "../../shared/util/getCtkAge";
import { buildReportJobPayload } from "../../shared/util/reportContent/buildReportJobPayload";
import { isHiringEvent } from "./components/core/HiringEvents/utils/isHiringEvent";
import { jobDetailsSectionModelFromVJModel } from "./components/core/ProfileInsights/components/PreferencesCollection/PreferencesCollectionModelFactory";
import { getEnvironmentConfig } from "./config/urls";
import { isRemoteLocationSafe } from "./util/remoteLocationUtil";
import {
  type ApplicantInsightsProctorData,
  getApplicantInsightsModel,
} from "./utils/applicantInsightsUtils";
import { type AutoClickApplyFlag, getAutoClickApplyFlags } from "./utils/autoClickApplyUtils";
import { buildCompensationModel } from "./utils/compensationUtils";
import {
  type EmployerInsightsProctorData,
  buildEmployerInsightsModel,
} from "./utils/employerInsightsUtils";
import { type BrandedJobProctorData, buildEmployerModel } from "./utils/employerUtils";
import { buildJobDetailsSectionModel } from "./utils/jobDetailsSectionUtils";
import { formatJobTypes } from "./utils/jobTypesUtils";
import { getOneGraphMatchComparisonResult } from "./utils/matchComparisonUtils";

import { Platform } from "react-native";

/**
 * Composes a URL with additional parameters, handling edge cases where both
 * may contain query string characters (? or &).
 *
 * @param baseUrl - The base URL which may or may not contain query parameters
 * @param additionalParams - Additional URL parameters which may start with ?, &, or neither
 * @returns The properly composed URL with all parameters
 */
export function composeUrlWithParams(
  baseUrl: string | null,
  additionalParams: string | null,
): string {
  if (!baseUrl) {
    return "";
  }

  if (!additionalParams) {
    return baseUrl;
  }

  try {
    const url = new URL(baseUrl);

    // This overwrites existing parameters with the same name
    new URLSearchParams(additionalParams).forEach((value, key) => {
      url.searchParams.set(key, value);
    });

    return url.toString();
  } catch (error) {
    console.error(`Error parsing URL (${baseUrl}): ${error as any}`);

    // Fallback to original string concatenation logic if URL parsing fails
    const normalizedParams = additionalParams.replace(/^[?&]/, "");
    if (!normalizedParams) {
      return baseUrl;
    }

    const baseHasParams = baseUrl.includes("?");
    if (baseHasParams) {
      // Base URL already has params, use & to append
      // Also handle case where base URL ends with & or ?
      const separator = baseUrl.endsWith("&") || baseUrl.endsWith("?") ? "" : "&";
      return baseUrl + separator + normalizedParams;
    } else {
      // Base URL has no params, use ? to start query string
      return baseUrl + "?" + normalizedParams;
    }
  }
}

interface ShareModel {
  url: string;
  message: string;
}

export interface RatingsModel {
  count: number;
  rating: number;
  countContent: string;
  ariaContent?: string;
}

interface BenefitsModel {
  benefits: {
    key: string;
    label: string;
  }[];
  benefitsCollapseThreshold: number;
}

export interface Location {
  formattedStreetAddress?: string;
  jobLatitude?: number;
  jobLongitude?: number;
  city?: string;
  admin1Code?: string;
  postalCode?: string;
}

export interface ReportContent {
  apiUrl: string;
  buildPayload: (reason: string, moreInfo: string) => string;
}

export interface Employer {
  key: string;
  companyName?: string;
  companyOverviewLink?: string;
  parentCompanyName?: string;
  ratings?: RatingsModel;
  companyReviewLink?: string;
  hideRating?: boolean;
  companyImagesModel?: CompanyImagesModel;
}

export interface CompanyImagesModel {
  logoUrl?: string;
  headerImageUrl?: string;
  logoImageOverlayLower: boolean;
  logoAltText: string;
  showBannerTop: boolean;
  showIconInTitle: boolean;
}

export interface TagModel {
  text: string;
}

interface Cookies {
  dsanr?: boolean;
}

interface Compensation {
  salaryInfoModel?: SalaryInfoModel;
}

export interface InterviewNowModel {
  interviewerCount?: number;
  existingApplicantUrl?: string | null;
}

export interface IndeedApplyButtonContainer {
  contentHtml: string;
  indeedApplyUrl: string;
  disabled: boolean;
  continueUrl?: string | undefined | null;
  additionalUrlParameters?: string | undefined | null;
  tmnUrl?: string | undefined;
}

export interface SavedJob {
  state?: string | null;
  applicationDraft?: {
    expiryTime?: number | null;
  } | null;
}

/**
 * Nexus (AI-powered application summary evaluation) disclaimer data, mirroring
 * jobseeker-frontend's `jobMetadataFooterModel.nexusModel`.
 */
export interface NexusDisclaimer {
  showNexusDisclaimer: boolean;
  accommodationEmail?: string;
  accommodationPhoneNumber?: string;
  accommodationUrl?: string;
}

/**
 * A facade data type that provides an interface aligned with a potential future OneGraph schema.
 * The main purpose is to isolate the ViewJobClientSideModel from the rest of the application.
 */
export interface ViewjobOneGraphModel {
  ctk: string;
  mobvjtk: string;
  accountKey?: string;
  shareModel: ShareModel;
  cookies: Cookies;
  ctaEnticer?: string;
  isSponsored: boolean;
  applicantInsightsModel?: ApplicantInsightsModel;
  employerInsightsModel?: component.employerinsights.EmployerInsightsModel;
  reportContent?: ReportContent;
  benefits?: BenefitsModel;
  interviewNowModel?: InterviewNowModel;
  nexusDisclaimer?: NexusDisclaimer;
  indeedApplyButtonContainer?: IndeedApplyButtonContainer;
  thirdPartyApply?: ViewJobThirdPartyApply;
  thirdPartyApplyCreateAccountModel?: component.accountcreation.ThirdPartyApplyCreateAccountModel;
  employerOutreachEngagement?: FindResumeEngagementData;
  additionalLinksViewModel?: component.additionallinks.AdditionalLinksViewModel;
  relatedLinks?: component.relatedlinks.RelatedLinksModel[];
  isAppBtnSticky?: boolean;
  structuredDataJobSummaryModel?: jobinfowrapper.jobinfo.StructuredDataJobSummaryModel;
  jobDuties?: string;
  isMobile?: boolean;
  shouldShowConditionalBackButton?: boolean;
  /**
   * Set when the user arrived from Google for Jobs (`utm_source=google_jobs_apply`)
   * AND the relevant `mob_g4j_auto_click_monetization_tst` proctor bucket is
   * active. Consumers auto-click the apply button and emit a `mobRcvFlag` log
   * with the flag's string value; `null` means no auto-click.
   *
   * The two variants are independent and each drives only its matching button
   * (mirroring jobseeker-frontend): `autoClickApplyIaFlag` -> Indeed Apply
   * button, `autoClickApplyNonIaFlag` -> third-party apply button.
   */
  autoClickApplyIaFlag: AutoClickApplyFlag | null;
  autoClickApplyNonIaFlag: AutoClickApplyFlag | null;
  job: {
    jobTitle: string;
    isExpired?: boolean;
    isHiringEvent?: boolean;
    details?: JobDetailsSectionModel;
    jobKey: string;
    isSponsored?: boolean;
    jobCountry?: string;
    jobLanguage?: string;
    jobOccupations?: string[];
    tags?: TagModel[];
    location?: Location;
    formattedLocation?: string;
    trueJobLocation?: string;
    remoteLocation?: boolean;
    remoteWorkModel?: {
      text?: string;
      type?: "REMOTE_ALWAYS" | "REMOTE_COVID_TEMPORARY" | "REMOTE_HYBRID";
      inlineText?: boolean;
    };
    jobDescription?: string;
    viewJobDisclaimer?: string;
    employerActivity?: string;
    isRetargetedCompany?: boolean;
    shouldClientSideLoadCompetitorsJobs?: boolean;
    datePublished?: number;
    jobType?: string;
    savedJob?: SavedJob;
  };
  features: {
    isJsPremiumApplicantInsightsTogActive: boolean;
    isJsPremiumApplicantInsightsFeedbackTstActive: boolean;
    isSimpleVjImproveActive: boolean;
    country: string;
    isCondensedRating: boolean;
    isApp?: boolean;
    crowtaxoFrontendTstPayload?: any;
    shouldAlwaysShowProfileInsights?: boolean;
    shouldShowTypicallyRequiredProfileInsights?: boolean;
    shouldMergeMceDataProfileInsights?: boolean;
    shouldUseMceAttributes?: boolean;
    isCommuteEstimateActive?: boolean;
    shouldHideAttributesWithoutRequirementStrength?: boolean;
    shouldHideStrictnessForInferredAttributes?: boolean;
    shouldLimitExpandedChipCount?: boolean;
    shouldOnlyDisplayMustHaveRequirements?: boolean;
    isVjInterviewPrepTstBlueBg: boolean;
    isVjInterviewPrepTstYellowBg: boolean;
    isVjResumeImprovementsCtaTstActive?: boolean;
    isJsProVjProInsightsTogActive?: boolean;
    isCompanyTabActive: boolean;
    isTrueJobLocationActive?: boolean;
  };
  employer: Employer;
  compensation: Compensation;
  jobSeeker: {
    matchComparison?: component.oneGraphMatchComparison.OneGraphMatchComparison; // this has to come from initialData.d.ts, as this mapped from a gql schema that can change anytime
    loggedIn: boolean;
    profile: {
      isProfileInsightsBlocked?: boolean;
      profileBaseUrl: string;
      hasResume: boolean;
      resumeType: string;
    };
    preferences?: {
      jobSeekerSuidsToFactIds: platform.model.JobSeekerSuidToFactId[];
    };
    isLoggedIn?: boolean;
    applyRateLimit?: boolean;
  };
  /**
   * Profile location data sourced from the main viewjob query. Resolved
   * so consumers don't need to know whether the address came from the
   * resume or default contact info.
   */
  jobSeekerProfileLocation?: {
    sourceLocation?: ProfileSourceLocation;
  };
  /**
   * Saved max-commute preference (minutes) sourced from the main viewjob
   * query. Only populated on the GraphQL path.
   */
  maxCommuteMinutes?: number;
}

type ProfileSourceLocation = NonNullable<
  NonNullable<
    NonNullable<NonNullable<ViewjobQuery["jobSeekerProfile"]>["profile"]>["resume"]
  >[number]
>["location"];

// Function to build the report payload with form data
const createReportPayload = (
  reportContentModel: component.reportcontent.ReportContentModel,
  reason: string,
  moreInfo: string,
): string =>
  buildReportJobPayload(
    {
      jobKey: reportContentModel.jobKey,
      hostId: reportContentModel.hostId,
      pageId: reportContentModel.pageId,
      ctk: reportContentModel.ctk,
      isLoggedIn: reportContentModel.isLoggedIn,
      isConfirmed: reportContentModel.isConfirmed,
      country: reportContentModel.country,
      language: reportContentModel.language,
      isMobile: reportContentModel.isMobile,
      userAgent: reportContentModel.userAgent,
      logEntryTK: reportContentModel.mobvjtk,
    },
    reason,
    moreInfo,
  );

const buildRclkValue = (
  jobKey: string | null | undefined,
  mobvjtk: string | null | undefined,
): string | null => {
  if (!jobKey || !mobvjtk) {
    return null;
  }

  const timestamp = getCtkTimestamp(mobvjtk);
  if (timestamp <= 0) {
    return null;
  }

  return new URLSearchParams({
    jk: jobKey,
    vjtk: mobvjtk,
    ts: timestamp.toString(),
    rd: "",
    qd: "",
  }).toString();
};

export const createViewjobOneGraphModels = (
  viewjobUrl: string,
  viewjob: NonNullable<ViewjobQuery["viewjob"]>,
  jobSeekerProfile: ViewjobQuery["jobSeekerProfile"],
  jobSeekerProfileStructuredData: ViewjobQuery["jobSeekerProfileStructuredData"],
  i18n: GettextInterface,
  tareData?: TareData,
  jobDataResult?: JobDataResultQuery,
  applicantInsightsProctorData?: ApplicantInsightsProctorData,
  employerInsightsProctorData?: EmployerInsightsProctorData,
  brandedJobProctorData?: BrandedJobProctorData,
): ViewjobOneGraphModel | null => {
  if (!viewjob.job) {
    return null;
  }
  let thirdPartyApply: ViewJobThirdPartyApply | undefined;
  let indeedApplyButtonContainer: IndeedApplyButtonContainer | undefined;
  const isApplied = !!viewjob.details?.iaApplied;

  if (viewjob.details?.applyMethod) {
    if (viewjob.details.applyMethod.__typename === "ViewJobThirdPartyApply") {
      thirdPartyApply = {
        href: viewjob.details.applyMethod.applyUrl ?? "",
        content: viewjob.details.applyMethod.content ?? "",
        appShareUrl: viewjob.details.applyMethod.appShareUrl ?? "",
        clickCookieName: CookieKey.desktopClickTrackingLog,
        clickCookieValue: buildRclkValue(viewjob.job.key, viewjob.details?.mobvjtk),
      };
    } else if (viewjob.details.applyMethod.__typename === "ViewJobIndeedApply") {
      // Note: indeedApplyUrl is now provided separately via context (applyLinkUrl)
      // and will be composed with additionalUrlParameters in the button component.
      // We set it to empty string here as a placeholder.
      indeedApplyButtonContainer = {
        contentHtml: viewjob.details.applyMethod.contentHtml ?? "",
        indeedApplyUrl: "", // Will be set via context applyLinkUrl
        disabled: isApplied,
        continueUrl: viewjob.details.applyMethod.continueUrl,
        additionalUrlParameters: viewjob.details.applyMethod.additionalUrlParameters,
        tmnUrl: viewjob.details.applyMethod.tmnUrl ?? undefined,
      };
    } else if (viewjob.details.applyMethod.__typename === "ViewJobHiringEvent") {
      indeedApplyButtonContainer = {
        contentHtml: viewjob.details.applyMethod.content ?? "",
        indeedApplyUrl: viewjob.details.applyMethod.indeedApplyUrl ?? "",
        disabled: isApplied,
      };
    }
  }

  const reportContentData = viewjob.details?.reportContent
    ? {
        apiUrl: viewjob.details.reportContent.apiUrl ?? "",
        buildPayload: (reason: string, moreInfo: string) => {
          const reportContentModel = JSON.parse(
            viewjob.details?.reportContent?.contentReportData ?? "{}",
          ) as component.reportcontent.ReportContentModel;
          return createReportPayload(reportContentModel, reason, moreInfo);
        },
      }
    : undefined;

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const proctorGroups = viewjob.details?.proctorGroups
    ? JSON.parse(viewjob.details.proctorGroups)
    : {};
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment
  const isJsPremiumParentTstActive = proctorGroups.isMosaicJsPremiumParentTstActive ?? false;
  // RN-5494: in the test bucket, source trueJobLocation / competitors flag from the
  // JRS Job.* fields instead of the legacy ViewJobDetails.* fields. See DISCO-5448.
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  const isJrsFieldsActive = proctorGroups.rn_vj_jrs_fields_tst === 1;

  // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access
  const jobOccupations = viewjob.job.occupations?.map((occupation: any) => occupation.key) ?? [];

  const jobSeekerSuidsToFactIds =
    viewjob.job.jobseekerPreferences?.knownPreferenceIds
      .filter((entry) => entry.suid && entry.id)
      .map((entry) => ({
        suid: entry.suid!,
        factId: entry.id!,
      })) || [];

  const buildEnvironment = tareData?.appModel?.buildEnvironment;
  const environmentConfig = getEnvironmentConfig(buildEnvironment);

  const viewjobUrlObj = new URL(viewjobUrl);
  const baseUrl = `${viewjobUrlObj.protocol}//${viewjobUrlObj.host}`;
  const shareUrl = new URL("/viewjob", baseUrl);
  shareUrl.searchParams.set("jk", viewjob.job.key);
  shareUrl.searchParams.set("from", Platform.OS === "ios" ? "appshareios" : "appshareandroid");

  // Resolve the user's preferred source location. The
  // resume location wins when a resume exists; otherwise fall back to the
  // default contact location.
  const profile = jobSeekerProfile?.profile;
  const mainResume = profile?.resume?.[0];
  const profileSourceLocation: ProfileSourceLocation | undefined = mainResume?.id
    ? (mainResume.location ?? undefined)
    : (profile?.defaultInfo?.contactInformation?.location ?? undefined);
  const jobSeekerProfileLocation = profile
    ? {
        sourceLocation: profileSourceLocation,
      }
    : undefined;

  let maxCommuteMinutes: number | undefined;
  for (const pref of jobSeekerProfileStructuredData?.preferences ?? []) {
    const minutes = pref?.maximumCommute?.timeMinutes;
    if (minutes && minutes > 0) {
      maxCommuteMinutes = minutes;
      break;
    }
  }

  return {
    ctk: tareData?.deviceModel?.ctk ?? "",
    mobvjtk: viewjob.details?.mobvjtk ?? "",
    accountKey: tareData?.accountStatus?.id ?? undefined,
    // TODO(RN-OG-migration): wire up auto-click-apply via a OneGraph field
    // once the schema exposes it. For now the OneGraph path doesn't know
    // about GFJ traffic, so we never auto-click on this code path.
    autoClickApplyIaFlag: null,
    autoClickApplyNonIaFlag: null,
    shareModel: {
      url: shareUrl.toString(),
      message: viewjob.job.title ?? "",
    },
    cookies: {
      dsanr: false,
    },
    applicantInsightsModel:
      isJsPremiumParentTstActive && jobSeekerProfile
        ? (getApplicantInsightsModel(jobSeekerProfile, viewjob, applicantInsightsProctorData) ??
          undefined)
        : undefined,
    employerInsightsModel:
      buildEmployerInsightsModel(
        isJsPremiumParentTstActive ? jobSeekerProfile : null,
        viewjob,
        employerInsightsProctorData,
        viewjob.details?.companySummary ?? undefined,
      ) ?? undefined,
    features: {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      isJsPremiumApplicantInsightsTogActive:
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        proctorGroups.isJsPremiumApplicantInsightsTogActive ?? false,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      isJsPremiumApplicantInsightsFeedbackTstActive:
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        proctorGroups.isJsPremiumApplicantInsightsFeedbackTstActive ?? false,
      isSimpleVjImproveActive: true, // !JP
      country: tareData?.appModel?.locale?.countryCode ?? "US",
      isCondensedRating: true, // !JP
      isApp: true,
      crowtaxoFrontendTstPayload: {
        vj_blackout_test: false,
      },
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      shouldAlwaysShowProfileInsights: proctorGroups.shouldAlwaysShowProfileInsights ?? false,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      shouldShowTypicallyRequiredProfileInsights:
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        proctorGroups.shouldShowTypicallyRequiredProfileInsights ?? false,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      shouldMergeMceDataProfileInsights:
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        proctorGroups.shouldMergeMceDataProfileInsights ?? false,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      shouldUseMceAttributes: proctorGroups.shouldUseMceAttributes ?? false,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      isCommuteEstimateActive: proctorGroups.isCommuteEstimateActive ?? false,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      shouldHideAttributesWithoutRequirementStrength:
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        proctorGroups.shouldHideAttributesWithoutRequirementStrength ?? false,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      shouldHideStrictnessForInferredAttributes:
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        proctorGroups.shouldHideStrictnessForInferredAttributes ?? false,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      shouldLimitExpandedChipCount: proctorGroups.shouldLimitExpandedChipCount ?? false,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      shouldOnlyDisplayMustHaveRequirements:
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        proctorGroups.shouldOnlyDisplayMustHaveRequirements ?? false,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      isVjInterviewPrepTstBlueBg: proctorGroups.isVjInterviewPrepTstBlueBg ?? false,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      isVjInterviewPrepTstYellowBg: proctorGroups.isVjInterviewPrepTstYellowBg ?? false,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      isVjResumeImprovementsCtaTstActive: proctorGroups.isVjResumeImprovementsCtaTstActive ?? false,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      isJsProVjProInsightsTogActive: proctorGroups.isJsProVjProInsightsTogActive ?? false,
      isCompanyTabActive: viewjob.details?.isCompanyTabActive ?? false,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      isTrueJobLocationActive: proctorGroups.disco_advertising_location_fe_tst ?? false,
    },
    employer: buildEmployerModel(i18n, viewjob, brandedJobProctorData),
    compensation: {
      salaryInfoModel: buildCompensationModel(viewjob),
    },
    jobSeeker: {
      loggedIn: tareData?.accountStatus?.isLoggedIn ?? false,
      profile: {
        profileBaseUrl: environmentConfig.profileBaseUrl,
        isProfileInsightsBlocked: viewjob.details?.profile?.isProfileInsightsBlocked ?? false,
        hasResume: !!mainResume?.id,
        resumeType: jobSeekerProfile?.profile?.resume?.[0]?.resumeType ?? "NO_RESUME",
      },
      isLoggedIn: tareData?.accountStatus?.isLoggedIn ?? false,
      applyRateLimit: viewjob.details?.isApplyRateLimited ?? false,
      matchComparison: getOneGraphMatchComparisonResult(
        jobDataResult?.jobData?.results[0]?.matchComparisonResult ?? undefined,
        true,
      ),
      preferences: {
        jobSeekerSuidsToFactIds,
      },
    },
    job: {
      jobTitle: viewjob.job.title ?? "",
      jobKey: viewjob.job.key ?? "",
      isSponsored: viewjob.details?.isSponsored ?? false,
      isExpired: viewjob.job.expired ?? false,
      jobLanguage: viewjob.job.language ?? undefined,
      jobOccupations,
      tags: [],
      jobDescription: viewjob.details?.sanitizedJobDescription ?? undefined,
      viewJobDisclaimer: viewjob.details?.disclaimerText ?? undefined,
      formattedLocation: viewjob.job.location?.formatted?.long ?? undefined,
      trueJobLocation:
        (isJrsFieldsActive ? viewjob.job.trueJobLocation : viewjob.details?.trueJobLocation) ??
        undefined,
      details: buildJobDetailsSectionModel(i18n, viewjob),
      jobCountry: viewjob.job.location?.countryCode ?? undefined,
      location: {
        formattedStreetAddress: viewjob.details?.location?.formattedStreetAddress ?? undefined,
        jobLatitude: viewjob.job.location?.latitude ?? undefined,
        jobLongitude: viewjob.job.location?.longitude ?? undefined,
        city: viewjob.job.location?.city ?? undefined,
        admin1Code: viewjob.job.location?.admin1Code ?? undefined,
        postalCode: viewjob.job.location?.postalCode ?? undefined,
      },
      remoteWorkModel: viewjob.details?.remoteWorkModel
        ? {
            text: viewjob.details?.remoteWorkModel?.text ?? undefined,
            inlineText: viewjob.details?.remoteWorkModel?.isInlineText ?? undefined,
            type: viewjob.details?.remoteWorkModel?.type ?? undefined,
          }
        : undefined,
      remoteLocation: isRemoteLocationSafe(
        viewjob.job.location?.latitude,
        viewjob.job.location?.longitude,
      ),
      isHiringEvent: isHiringEvent(viewjob),
      shouldClientSideLoadCompetitorsJobs:
        (isJrsFieldsActive
          ? viewjob.job.shouldCSRCompetitorsJobs
          : viewjob.details?.shouldDisplayCompetitorsJobs) ?? false,
      datePublished: viewjob.job.datePublished,
      jobType: formatJobTypes(viewjob.details?.jobTypeAndShiftSchedule?.jobTypes),
      savedJob: viewjob.job.savedJob ?? undefined,
    },
    thirdPartyApply,
    indeedApplyButtonContainer,
    reportContent: reportContentData,
    ctaEnticer: viewjob.details?.ctaEnticer ?? undefined,
    isSponsored: viewjob.details?.isSponsored ?? false,
    benefits: viewjob.details?.benefit
      ? {
          benefits: (viewjob.details.benefit.benefits ?? []).map((benefit) => ({
            key: benefit.key,
            label: benefit.label,
          })),
          benefitsCollapseThreshold: viewjob.details.benefit.benefitsCollapseThreshold,
        }
      : undefined,
    // depends on rn_non_ia_cta_tst which has been rolled out
    isAppBtnSticky: true,
    interviewNowModel: viewjob.details?.interviewNowModel
      ? {
          interviewerCount: viewjob.details.interviewNowModel?.InterviewerCount ?? undefined,
          existingApplicantUrl: viewjob.details.interviewNowModel?.existingApplicantUrl ?? null,
        }
      : undefined,
    nexusDisclaimer:
      viewjob.job.showNexusDisclaimer === undefined
        ? undefined
        : {
            showNexusDisclaimer: viewjob.job.showNexusDisclaimer,
            accommodationUrl: viewjob.job.nexusAccommodation?.url ?? undefined,
            accommodationPhoneNumber: viewjob.job.nexusAccommodation?.phoneNumber ?? undefined,
            accommodationEmail: viewjob.job.nexusAccommodation?.email ?? undefined,
          },
    structuredDataJobSummaryModel: {
      jobKey: viewjob.job.key,
      salary: viewjob.job.compensation?.formattedText ?? undefined,
      benefits: viewjob.details?.benefit?.benefits?.map((benefit) => benefit.label),
      shiftAndSchedule: viewjob.details?.jobTypeAndShiftSchedule?.shiftAndSchedule ?? undefined,
      jobTypeAndShiftSectionApp: {
        jobType: viewjob.details?.jobTypeAndShiftSchedule?.jobTypes ?? undefined,
        shiftAndSchedule: viewjob.details?.jobTypeAndShiftSchedule?.shiftAndSchedule ?? undefined,
      },
      commuteTimeMinutes: viewjob.job.commute?.durationMinutes ?? undefined,
    },
    jobSeekerProfileLocation,
    maxCommuteMinutes,
  };
};

export const createViewjobOneGraphModel = (
  viewjobModel: model.viewjob.ViewJobClientSideModel,
  locale: Locale,
): ViewjobOneGraphModel => {
  const shareUrl = new URL(viewjobModel.baseUrl);
  // the basename is "https://www.indeed.com:443" so we need to append /viewjob
  shareUrl.pathname = "/viewjob";
  shareUrl.searchParams.set("jk", viewjobModel.jobKey);
  shareUrl.searchParams.set("from", Platform.OS === "ios" ? "appshareios" : "appshareandroid");

  // Build reportContent with apiUrl and buildPayload function
  const reportContentData = viewjobModel.reportContentModel
    ? {
        apiUrl: viewjobModel.reportContentModel.reportContentApiUrl,
        buildPayload: (reason: string, moreInfo: string) =>
          createReportPayload(viewjobModel.reportContentModel, reason, moreInfo),
      }
    : undefined;

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const tagsModel =
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    viewjobModel.jobInfoWrapperModel?.jobInfoModel?.jobInfoHeaderModel?.tagModels ?? [];

  const country = locale.country ?? "";
  const isJapan = country === "JP";
  const isCondensedRating = !isJapan;
  const jobTitle = viewjobModel.jobTitle;

  let thirdPartyApply: ViewJobThirdPartyApply | undefined;
  let thirdPartyApplyCreateAccountModel:
    | component.accountcreation.ThirdPartyApplyCreateAccountModel
    | undefined;
  if (viewjobModel.viewJobButtonLinkContainerModel != null) {
    thirdPartyApply = {
      href: viewjobModel.viewJobButtonLinkContainerModel.viewJobButtonLinkModel.href,
      content:
        viewjobModel.viewJobButtonLinkContainerModel.viewJobButtonLinkModel.contentHtmlRn ?? "",
      appShareUrl: viewjobModel.appShareUrl,
      clickCookieName: viewjobModel.viewJobButtonLinkContainerModel.clickCookieName ?? null,
      clickCookieValue: viewjobModel.viewJobButtonLinkContainerModel.clickCookieValue ?? null,
    };
    thirdPartyApplyCreateAccountModel = {
      contextualSignInPassportURL:
        viewjobModel.viewJobButtonLinkContainerModel.thirdPartyApplyCreateAccountModel
          ?.contextualSignInPassportURL ?? "",
      accountSignInPassportURL:
        viewjobModel.viewJobButtonLinkContainerModel.thirdPartyApplyCreateAccountModel
          ?.accountSignInPassportURL ?? "",
      baseSecureUrl:
        viewjobModel.viewJobButtonLinkContainerModel.thirdPartyApplyCreateAccountModel
          ?.baseSecureUrl ?? "",
      closeAriaText:
        viewjobModel.viewJobButtonLinkContainerModel.thirdPartyApplyCreateAccountModel
          ?.closeAriaText ?? "",
      continueButtonText:
        viewjobModel.viewJobButtonLinkContainerModel.thirdPartyApplyCreateAccountModel
          ?.continueButtonText ?? "",
      createAccountButtonText:
        viewjobModel.viewJobButtonLinkContainerModel.thirdPartyApplyCreateAccountModel
          ?.createAccountButtonText ?? "",
      createAccountReasonText:
        viewjobModel.viewJobButtonLinkContainerModel.thirdPartyApplyCreateAccountModel
          ?.createAccountReasonText ?? "",
      createAccountPassportURL:
        viewjobModel.viewJobButtonLinkContainerModel.thirdPartyApplyCreateAccountModel
          ?.createAccountPassportURL ?? "",
      fromParam:
        viewjobModel.viewJobButtonLinkContainerModel.thirdPartyApplyCreateAccountModel?.fromParam ??
        "",
      createAccountBottomText:
        viewjobModel.viewJobButtonLinkContainerModel.thirdPartyApplyCreateAccountModel
          ?.createAccountBottomText ?? "",
      haveAnAccountText:
        viewjobModel.viewJobButtonLinkContainerModel.thirdPartyApplyCreateAccountModel
          ?.haveAnAccountText ?? "",
      signInText:
        viewjobModel.viewJobButtonLinkContainerModel.thirdPartyApplyCreateAccountModel
          ?.signInText ?? "",
      titleText:
        viewjobModel.viewJobButtonLinkContainerModel.thirdPartyApplyCreateAccountModel?.titleText ??
        "",
    };
  }

  // Determine if the Indeed Apply button should be disabled
  const isIndeedApplyDisabled =
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    viewjobModel.jobInfoWrapperModel?.jobInfoModel?.appliedStateBannerModel?.iaApplied === true;

  return {
    ctk: viewjobModel.ctk,
    mobvjtk: viewjobModel.mobtk,
    accountKey: viewjobModel.accountKey,
    ...getAutoClickApplyFlags({
      autoClickApply: viewjobModel.autoClickApply,
      autoClickApplyNonIa: viewjobModel.autoClickApplyNonIa,
    }),
    cookies: {
      dsanr: viewjobModel.dsanr,
    },
    shareModel: {
      message: jobTitle,
      url: shareUrl.toString(),
    },
    applicantInsightsModel: viewjobModel.applicantInsightsModel,
    employerInsightsModel: viewjobModel.employerInsightsModel,
    employerOutreachEngagement: viewjobModel.employerOutreachEngagementModel,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    structuredDataJobSummaryModel:
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      viewjobModel.jobInfoWrapperModel?.jobInfoModel?.structuredDataJobSummaryModel,

    jobDuties: viewjobModel.jobDuties,
    reportContent: reportContentData,
    ctaEnticer: viewjobModel.ctaEnticer,
    isSponsored: viewjobModel.sponsored,
    benefits: viewjobModel.benefitsModel,
    additionalLinksViewModel: viewjobModel.additionalLinksViewModel,
    relatedLinks: viewjobModel.relatedLinks,
    thirdPartyApply,
    thirdPartyApplyCreateAccountModel,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    interviewNowModel: viewjobModel.interviewNowModel,
    nexusDisclaimer: viewjobModel.jobMetadataFooterModel?.nexusModel,
    isAppBtnSticky:
      // This was originally driven by the data model, but since this is a rolled-out test the data model
      // is essentially just checking whether the request is mobile (see
      // https://indeed.sourcegraph.com/r/code.corp.indeed.com/jobsearch/indeedmobile/-/blob/src/main/java/com/indeed/mobileweb/basecamp/model/shared/component/buttons/util/StickyButtonUtil.java?L36).
      // Since we also want sticky behavior for desktop this will just always be true
      // viewjobModel.viewJobButtonLinkContainerModel?.viewJobButtonLinkModel?.rnSticky ?? true,
      true,
    indeedApplyButtonContainer: viewjobModel.indeedApplyButtonContainer
      ? {
          contentHtml:
            viewjobModel.indeedApplyButtonContainer.indeedApplyButtonModel?.contentHtml ?? "",
          indeedApplyUrl: viewjobModel.indeedApplyButtonContainer.indeedApplyUrl ?? "",
          disabled: isIndeedApplyDisabled,
          tmnUrl: viewjobModel.indeedApplyButtonContainer.tmnUrl,
        }
      : undefined,
    isMobile: !viewjobModel.desktop,
    shouldShowConditionalBackButton: viewjobModel.shouldShowConditionalBackButton,
    job: {
      jobTitle,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      isExpired: viewjobModel.jobInfoWrapperModel?.jobInfoModel?.showExpiredHeader,
      isHiringEvent: viewjobModel.isHiringEvent,
      details: jobDetailsSectionModelFromVJModel(viewjobModel),
      jobKey: viewjobModel.jobKey,
      isSponsored: viewjobModel.sponsored ?? false,
      jobCountry: viewjobModel.jobCountry,
      jobLanguage: viewjobModel.jobLanguage,
      jobOccupations: viewjobModel.jobOccupations,

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      tags: tagsModel.map((tag: component.tag.TagModel) => ({ text: tag.text })),
      location: viewjobModel.commuteInfoModel
        ? {
            formattedStreetAddress: viewjobModel.commuteInfoModel.formattedStreetAddress,
            jobLatitude: viewjobModel.commuteInfoModel.jobLatitude,
            jobLongitude: viewjobModel.commuteInfoModel.jobLongitude,
          }
        : undefined,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      formattedLocation:
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        viewjobModel.jobInfoWrapperModel?.jobInfoModel?.jobInfoHeaderModel?.formattedLocation,
      trueJobLocation: viewjobModel.trueJobLocation,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      remoteLocation:
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        viewjobModel.jobInfoWrapperModel?.jobInfoModel?.jobInfoHeaderModel?.remoteLocation,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      remoteWorkModel:
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        viewjobModel.jobInfoWrapperModel?.jobInfoModel?.jobInfoHeaderModel?.remoteWorkModel,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      jobDescription: viewjobModel.jobInfoWrapperModel?.jobInfoModel?.sanitizedJobDescription,
      viewJobDisclaimer:
        viewjobModel.viewJobButtonLinkContainerModel?.viewJobButtonLinkModel.disclaimer,
      isRetargetedCompany: viewjobModel.isRetargetedCompany,
      shouldClientSideLoadCompetitorsJobs: viewjobModel.shouldClientSideLoadCompetitorsJobs,
      datePublished: viewjobModel.datePublished,
      jobType: formatJobTypes(
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-argument
        viewjobModel.jobInfoWrapperModel?.jobInfoModel?.jobDescriptionSectionModel
          ?.jobDetailsSection?.jobTypes,
      ),
      savedJob: viewjobModel.savedJob,
    },
    features: {
      isJsPremiumApplicantInsightsTogActive: viewjobModel.clientsideProctorGroups
        ? viewjobModel.clientsideProctorGroups.isJsPremiumApplicantInsightsTogActive
        : false,
      isJsPremiumApplicantInsightsFeedbackTstActive: viewjobModel.clientsideProctorGroups
        ? viewjobModel.clientsideProctorGroups.isJsPremiumApplicantInsightsFeedbackTstActive
        : false,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      isSimpleVjImproveActive:
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        viewjobModel.jobInfoWrapperModel?.jobInfoModel?.jobInfoHeaderModel?.isSimpleVjImproveActive,
      country,
      isApp: viewjobModel.isApp,
      isCondensedRating,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      crowtaxoFrontendTstPayload: viewjobModel.crowtaxoFrontendTstPayload,
      shouldAlwaysShowProfileInsights:
        viewjobModel.clientsideProctorGroups?.shouldAlwaysShowProfileInsights,
      shouldShowTypicallyRequiredProfileInsights:
        viewjobModel.clientsideProctorGroups?.shouldShowTypicallyRequiredProfileInsights,
      shouldMergeMceDataProfileInsights:
        viewjobModel.clientsideProctorGroups?.shouldMergeMceDataProfileInsights,
      shouldUseMceAttributes: viewjobModel.clientsideProctorGroups?.shouldUseMceAttributes,
      isCommuteEstimateActive: viewjobModel.clientsideProctorGroups?.isCommuteEstimateActive,
      shouldHideAttributesWithoutRequirementStrength:
        viewjobModel.clientsideProctorGroups?.shouldHideAttributesWithoutRequirementStrength,
      shouldHideStrictnessForInferredAttributes:
        viewjobModel.clientsideProctorGroups?.shouldHideStrictnessForInferredAttributes,
      shouldLimitExpandedChipCount:
        viewjobModel.clientsideProctorGroups?.shouldLimitExpandedChipCount,
      shouldOnlyDisplayMustHaveRequirements:
        viewjobModel.clientsideProctorGroups?.shouldOnlyDisplayMustHaveRequirements,
      isVjInterviewPrepTstBlueBg:
        viewjobModel.clientsideProctorGroups?.isVjInterviewPrepTstBlueBg ?? false,
      isVjInterviewPrepTstYellowBg:
        viewjobModel.clientsideProctorGroups?.isVjInterviewPrepTstYellowBg ?? false,
      isVjResumeImprovementsCtaTstActive:
        viewjobModel.clientsideProctorGroups?.isVjResumeImprovementsCtaTstActive ?? false,
      isJsProVjProInsightsTogActive:
        viewjobModel.clientsideProctorGroups?.isJsProVjProInsightsTogActive ?? false,
      isCompanyTabActive: !!viewjobModel.companyTabModel,
      isTrueJobLocationActive:
        viewjobModel.clientsideProctorGroups?.disco_advertising_location_fe_tst ?? false,
    },
    employer: {
      key: viewjobModel.companyTabModel?.fccKey ?? "",
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      companyName:
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        viewjobModel.jobInfoWrapperModel?.jobInfoModel?.jobInfoHeaderModel?.companyName,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      companyOverviewLink:
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        viewjobModel.jobInfoWrapperModel?.jobInfoModel?.jobInfoHeaderModel?.companyOverviewLink,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      parentCompanyName:
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        viewjobModel.jobInfoWrapperModel?.jobInfoModel?.jobInfoHeaderModel?.parentCompanyName,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      ratings:
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        viewjobModel.jobInfoWrapperModel?.jobInfoModel?.jobInfoHeaderModel?.ratingsModel ??
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        viewjobModel.jobInfoWrapperModel?.jobInfoModel?.jobInfoHeaderModel?.companyReviewModel
          ?.ratingsModel,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      companyReviewLink:
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        viewjobModel.jobInfoWrapperModel?.jobInfoModel?.jobInfoHeaderModel?.companyReviewLink,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      hideRating:
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        viewjobModel.jobInfoWrapperModel?.jobInfoModel?.jobInfoHeaderModel?.hideRating,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      companyImagesModel:
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        viewjobModel.jobInfoWrapperModel?.jobInfoModel?.jobInfoHeaderModel?.companyImagesModel,
    },
    compensation: {
      salaryInfoModel: viewjobModel.salaryInfoModel,
    },
    jobSeeker: {
      matchComparison: viewjobModel.oneGraphMatchComparison,
      loggedIn: viewjobModel.loggedIn,
      profile: {
        isProfileInsightsBlocked: viewjobModel.isProfileInsightsBlocked,
        profileBaseUrl: viewjobModel.profileBaseUrl,
        hasResume: false,
        resumeType: "NO_RESUME",
      },
      preferences: {
        jobSeekerSuidsToFactIds: viewjobModel.jobSeekerSuidsToFactIds || [],
      },
      isLoggedIn: viewjobModel.loggedIn,
      applyRateLimit: viewjobModel.applyRateLimited,
    },
  };
};
